/**
 * Helpers for the clinician dashboard's sessionStorage copy of patient summaries.
 *
 * Background for new developers
 * -----------------------------
 * GlobalStateContext downloads one `patient_summaries` document per patient and
 * keeps the list in React state so the Current/Archived Patients tables render
 * instantly. It also writes that list to sessionStorage so a browser refresh
 * can paint the table before the network round-trip finishes.
 *
 * Each summary carries two kinds of data:
 *
 *   1. Table data the dashboard actually renders or filters on (name, email,
 *      clinicians, progress pills, archive state, `reportsProgress` for the
 *      quick-filter chips, and so on).
 *   2. Analytics enrichment that only the internal admin dashboard reads
 *      (server side, via projected queries). The SPA never looks at these.
 *
 * The Firestore web SDK cannot project fields, so the browser always downloads
 * the whole document. What we *can* control is what we persist. Stripping the
 * analytics fields before writing to sessionStorage does two useful things:
 *
 *   - Keeps the cache under the ~5 MB per-origin quota for longer. The persist
 *     is best-effort (wrapped in try/catch), so exceeding the quota only loses
 *     the instant cold-load paint, but there is no reason to spend the budget
 *     on bytes the dashboard never reads.
 *   - Keeps special-category data (for example ethnicity or sexual orientation
 *     in `demographicsSummary`) out of browser storage at rest. The same
 *     clinician can still open the patient record, so this is data
 *     minimisation rather than access control.
 *
 * The in-memory list is NOT stripped: it is the server's shape and is replaced
 * wholesale on every refetch. Only the sessionStorage copy is reduced, and
 * only by fields nothing in `src/` consumes. If a dashboard feature ever needs
 * one of these fields, remove it from the list below (and it will then be
 * hydrated on cold load too).
 */

/**
 * Summary fields that exist purely for the admin analytics dashboard.
 * `reportsProgress` is deliberately NOT here: the quick-filter chips in
 * `patientFilterUtils.js` read `reportsProgress.pr.status`.
 */
export const ADMIN_ANALYTICS_SUMMARY_FIELDS = Object.freeze([
  'demographicsSummary',
  'diagnosticDecisionSummary',
  'formFeedbackSummary',
  'informantFeedbackSummary',
  'formProgress',
  'informantsSummary',
  'reportTimestamps',
]);

/**
 * Return a shallow copy of `summary` without the admin-only analytics fields.
 *
 * Pure and defensive: non-object input is returned unchanged so a malformed
 * list entry cannot break the (best-effort) cache write. Fields that are
 * absent, null or undefined are simply not present in the result, matching how
 * an optimistic stub (which sets them to null) and a legacy summary (which
 * lacks them entirely) both already look to the dashboard.
 *
 * @param {object} summary One row from `patient_summaries` (or an optimistic stub).
 * @returns {object} The same row minus ADMIN_ANALYTICS_SUMMARY_FIELDS.
 */
export function stripAdminAnalyticsFields(summary) {
  if (!summary || typeof summary !== 'object' || Array.isArray(summary)) return summary;
  const out = { ...summary };
  for (const key of ADMIN_ANALYTICS_SUMMARY_FIELDS) {
    delete out[key];
  }
  return out;
}
